'use client';

import { useState } from 'react';
import Link from 'next/link';

// ── Types ─────────────────────────────────────────────────────────────────────

type Mode = 'raised-bed' | 'containers';
type ContainerShape = 'round' | 'square' | 'window-box' | 'grow-bag';

// ── Presets ───────────────────────────────────────────────────────────────────

interface ContainerPreset {
  label: string;
  shape: ContainerShape;
  diameter?: number;
  length?: number;
  width?: number;
  height: number;
}

const CONTAINER_PRESETS: ContainerPreset[] = [
  { label: '6 inch pot',         shape: 'round',      diameter: 6,   height: 6  },
  { label: '10 inch pot',        shape: 'round',      diameter: 10,  height: 9  },
  { label: '12 inch pot',        shape: 'round',      diameter: 12,  height: 10 },
  { label: '15 gallon grow bag', shape: 'grow-bag',   diameter: 16,  height: 15 },
  { label: '24 inch window box', shape: 'window-box', length: 24, width: 8, height: 8 },
  { label: 'Half wine barrel',   shape: 'square',     length: 24, width: 24, height: 18 },
];

// ── Math helpers ──────────────────────────────────────────────────────────────

function raisedBedCubicFeet(length: number, width: number, depthInches: number, beds: number): number {
  return length * width * (depthInches / 12) * beds;
}

function containerCubicFeet(
  shape: ContainerShape,
  diameter: number,
  length: number,
  width: number,
  heightInches: number,
): number {
  if (shape === 'round' || shape === 'grow-bag') {
    const r = diameter / 2 / 12; // radius in feet
    const h = heightInches / 12;
    return Math.PI * r * r * h;
  }
  // square / rectangular / window-box
  return (length / 12) * (width / 12) * (heightInches / 12);
}

function ceilBags(cubicFeet: number, bagSize: number): number {
  return Math.ceil(cubicFeet / bagSize);
}

// ── Stepper input ─────────────────────────────────────────────────────────────

function Stepper({
  label,
  value,
  onChange,
  min = 0,
  unit,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  unit?: string;
}) {
  return (
    <div>
      <label className="block font-body text-[12px] font-semibold text-sage uppercase tracking-wide mb-1.5">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          className="w-9 h-9 rounded-lg border border-sand bg-white font-body text-[18px]
                     text-dark-text hover:bg-light-sage hover:border-sage transition-colors
                     flex items-center justify-center flex-shrink-0"
        >
          −
        </button>
        <div className="flex-1 relative">
          <input
            type="number"
            value={value}
            onChange={e => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v) && v >= min) onChange(v);
            }}
            min={min}
            className="w-full font-body text-[15px] text-dark-text bg-white border border-sand
                       rounded-lg px-3 py-2 text-center focus:outline-none focus:border-sage
                       transition-colors"
          />
          {unit && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 font-body text-[12px] text-sage pointer-events-none">
              {unit}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          className="w-9 h-9 rounded-lg border border-sand bg-white font-body text-[18px]
                     text-dark-text hover:bg-light-sage hover:border-sage transition-colors
                     flex items-center justify-center flex-shrink-0"
        >
          +
        </button>
      </div>
    </div>
  );
}

// ── Results card ──────────────────────────────────────────────────────────────

function ResultsCard({
  cubicFeet,
  depthLabel,
  sqFtLabel,
}: {
  cubicFeet: number;
  depthLabel?: string;
  sqFtLabel?: string;
}) {
  const cubicYards = cubicFeet / 27;
  const smallBags  = ceilBags(cubicFeet, 0.75);
  const largeBags  = ceilBags(cubicFeet, 1.5);
  const xlBags     = ceilBags(cubicFeet, 2);

  const displayCf = cubicFeet < 0.1 ? '< 0.1' : cubicFeet.toFixed(1);
  const displayYd = cubicYards.toFixed(2);

  return (
    <div className="bg-white rounded-xl border border-sand p-5 space-y-4">
      <h3 className="font-heading text-[18px] text-warm-brown">You need approximately</h3>

      {/* Cubic feet */}
      <div className="border-b border-[#F0EBE0] pb-4">
        <p className="font-body text-[13px] font-semibold text-sage uppercase tracking-wide mb-1">
          Total Volume
        </p>
        <p className="font-heading text-[28px] text-dark-text leading-none">
          {displayCf} <span className="text-[18px] font-body font-normal text-sage">cubic feet</span>
        </p>
      </div>

      {/* Bags */}
      <div className="border-b border-[#F0EBE0] pb-4 space-y-2">
        <p className="font-body text-[13px] font-semibold text-sage uppercase tracking-wide mb-1">
          Standard Bags
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#FAFAF9] rounded-lg border border-sand p-3 text-center">
            <p className="font-heading text-[22px] text-dark-text">{smallBags}</p>
            <p className="font-body text-[11px] text-sage mt-0.5">Small bags</p>
            <p className="font-body text-[10px] text-sand">0.75 cu ft</p>
          </div>
          <div className="bg-[#FAFAF9] rounded-lg border border-sand p-3 text-center">
            <p className="font-heading text-[22px] text-dark-text">{largeBags}</p>
            <p className="font-body text-[11px] text-sage mt-0.5">Large bags</p>
            <p className="font-body text-[10px] text-sand">1.5 cu ft</p>
          </div>
          <div className="bg-[#FAFAF9] rounded-lg border border-sand p-3 text-center">
            <p className="font-heading text-[22px] text-dark-text">{xlBags}</p>
            <p className="font-body text-[11px] text-sage mt-0.5">XL bags</p>
            <p className="font-body text-[10px] text-sand">2 cu ft</p>
          </div>
        </div>
      </div>

      {/* Bulk */}
      <div className="space-y-1.5">
        <p className="font-body text-[13px] font-semibold text-sage uppercase tracking-wide mb-1">
          Bulk Delivery
        </p>
        <p className="font-heading text-[22px] text-dark-text leading-none">
          {cubicYards < 1 ? '< 1' : displayYd}{' '}
          <span className="text-[16px] font-body font-normal text-sage">cubic yards</span>
        </p>
        {sqFtLabel && depthLabel && (
          <p className="font-body text-[13px] text-sage">
            One yard fills approximately {sqFtLabel} at {depthLabel} deep.
          </p>
        )}
        {cubicYards < 1 ? (
          <p className="font-body text-[13px] text-sage italic">
            Less than 1 yard — bags are more practical for this size.
          </p>
        ) : cubicYards >= 2 ? (
          <p className="font-body text-[13px] text-deep-green font-semibold">
            Consider bulk delivery — it is usually more economical than bags at this volume.
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ── Raised Bed mode ───────────────────────────────────────────────────────────

function RaisedBedCalculator() {
  const [length, setLength] = useState(4);
  const [width,  setWidth]  = useState(8);
  const [depth,  setDepth]  = useState(12);
  const [beds,   setBeds]   = useState(1);

  const cubicFeet = raisedBedCubicFeet(length, width, depth, beds);
  const sqFtPerYard = Math.round(27 / (depth / 12));
  const depthLabel = depth >= 12 ? `${depth} inches` : `${depth} in`;
  const sqFtLabel  = `${sqFtPerYard} sq ft`;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <Stepper label="Length" value={length} onChange={setLength} min={1} unit="ft" />
        <Stepper label="Width"  value={width}  onChange={setWidth}  min={1} unit="ft" />
        <Stepper label="Depth"  value={depth}  onChange={setDepth}  min={1} unit="in" />
        <Stepper label="Number of beds" value={beds} onChange={setBeds} min={1} />
      </div>

      <ResultsCard
        cubicFeet={cubicFeet}
        depthLabel={depthLabel}
        sqFtLabel={sqFtLabel}
      />

      <div className="bg-light-sage border border-[#C5D9B8] rounded-xl px-5 py-4">
        <p className="font-body text-[14px] text-sage leading-relaxed">
          For new beds, plan to mix in 2–3 inches of compost on top of your soil. Add that
          to your depth calculation for the most accurate estimate. Healthy soil is the
          foundation of everything.
        </p>
      </div>
    </div>
  );
}

// ── Container mode ────────────────────────────────────────────────────────────

function ContainerCalculator() {
  const [shape,     setShape]     = useState<ContainerShape>('round');
  const [diameter,  setDiameter]  = useState(12);
  const [length,    setLength]    = useState(24);
  const [width,     setWidth]     = useState(12);
  const [height,    setHeight]    = useState(12);
  const [count,     setCount]     = useState(1);

  function applyPreset(preset: ContainerPreset) {
    setShape(preset.shape);
    if (preset.diameter) setDiameter(preset.diameter);
    if (preset.length)   setLength(preset.length);
    if (preset.width)    setWidth(preset.width);
    setHeight(preset.height);
    setCount(1);
  }

  const isRound = shape === 'round' || shape === 'grow-bag';
  const singleCf = containerCubicFeet(shape, diameter, length, width, height);
  const totalCf  = singleCf * count;

  const SHAPE_OPTIONS: { value: ContainerShape; label: string }[] = [
    { value: 'round',      label: 'Round pot'        },
    { value: 'square',     label: 'Square/rectangle' },
    { value: 'window-box', label: 'Window box'       },
    { value: 'grow-bag',   label: 'Grow bag'         },
  ];

  return (
    <div className="space-y-5">

      {/* Container shape */}
      <div>
        <label className="block font-body text-[12px] font-semibold text-sage uppercase tracking-wide mb-2">
          Container Type
        </label>
        <div className="flex flex-wrap gap-2">
          {SHAPE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setShape(opt.value)}
              className={`font-body text-[13px] font-semibold px-4 py-2 rounded-full border-2 transition-all
                ${shape === opt.value
                  ? 'bg-deep-green text-white border-deep-green'
                  : 'bg-white text-dark-text border-sand hover:border-sage'
                }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quick presets */}
      <div>
        <p className="font-body text-[12px] font-semibold text-sage uppercase tracking-wide mb-2">
          Common Sizes
        </p>
        <div className="flex flex-wrap gap-2">
          {CONTAINER_PRESETS.map(preset => (
            <button
              key={preset.label}
              type="button"
              onClick={() => applyPreset(preset)}
              className="font-body text-[13px] text-dark-text bg-white border border-sand
                         px-3 py-1.5 rounded-full hover:bg-light-sage hover:border-sage
                         transition-colors"
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Dimensions */}
      <div className="grid grid-cols-2 gap-4">
        {isRound ? (
          <Stepper label="Diameter" value={diameter} onChange={setDiameter} min={1} unit="in" />
        ) : (
          <>
            <Stepper label="Length" value={length} onChange={setLength} min={1} unit="in" />
            <Stepper label="Width"  value={width}  onChange={setWidth}  min={1} unit="in" />
          </>
        )}
        <Stepper label="Height / Depth" value={height} onChange={setHeight} min={1} unit="in" />
        <Stepper label="Number of containers" value={count} onChange={setCount} min={1} />
      </div>

      <ResultsCard cubicFeet={totalCf} />

      <div className="bg-light-sage border border-[#C5D9B8] rounded-xl px-5 py-4">
        <p className="font-body text-[14px] text-sage leading-relaxed">
          For new beds, plan to mix in 2–3 inches of compost on top of your soil. Add that
          to your depth calculation for the most accurate estimate. Healthy soil is the
          foundation of everything.
        </p>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function SoilCalculatorPage() {
  const [mode, setMode] = useState<Mode>('raised-bed');

  return (
    <div className="max-w-2xl">

      {/* Header */}
      <div className="mb-2">
        <h1 className="font-heading text-[28px] text-dark-text leading-tight">
          Soil Volume Calculator
        </h1>
        <p className="font-body text-[16px] text-sage mt-1">
          How much soil do you need? Find out in seconds.
        </p>
      </div>

      {/* Callout note */}
      <div className="bg-light-sage border border-[#C5D9B8] rounded-xl px-5 py-4 mb-6">
        <p className="font-body text-[14px] text-sage leading-relaxed">
          Whether you are filling a raised bed, a container, or a series of pots, this
          calculator tells you exactly how much soil to buy — in cubic feet, standard bags,
          and bulk yards.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setMode('raised-bed')}
          className={`font-body text-[14px] font-semibold px-5 py-2.5 rounded-full transition-all
            ${mode === 'raised-bed'
              ? 'bg-deep-green text-white'
              : 'bg-light-sage text-sage hover:text-deep-green'
            }`}
        >
          Raised Bed or In-Ground
        </button>
        <button
          onClick={() => setMode('containers')}
          className={`font-body text-[14px] font-semibold px-5 py-2.5 rounded-full transition-all
            ${mode === 'containers'
              ? 'bg-deep-green text-white'
              : 'bg-light-sage text-sage hover:text-deep-green'
            }`}
        >
          Containers and Pots
        </button>
      </div>

      {/* Calculator */}
      {mode === 'raised-bed' ? <RaisedBedCalculator /> : <ContainerCalculator />}

      {/* Back link */}
      <div className="mt-8 pt-6 border-t border-sand">
        <Link href="/templates" className="font-body text-[13px] text-sage hover:text-deep-green transition-colors">
          ← Back to Garden Templates
        </Link>
      </div>

    </div>
  );
}
